import { Components, Theme } from '@mui/material'

declare module '@mui/material/MenuItem' {
  interface MenuItemOwnProps {
    color?:
      /** The menu's own call to action — the item that adds or creates rather than picks one of the options. */
      | 'primary'
      | 'danger'
      // @deprecated - to be removed in favor of the semantic 'danger'
      | 'red'
  }
}

export const menuContainerStyle = (theme: Theme) => ({
  minWidth: 200,
  marginTop: theme.spacing(1.5),
  boxShadow: theme.customShadows.sm,
  // Horizontal padding is on the container not the list, so it's in the right place for scrollbars
  padding: theme.spacing(0, 1),
  borderRadius: theme.shape['radius-md'],
  backgroundColor: theme.palette.bg.surface.elevated,
  backgroundImage: 'none', // MUI applies a default background image for Paper
  '& .MuiMenu-list': menuListStyle(theme),
})

export const menuListStyle = (theme: Theme) => ({
  // Vertical padding is on the list, not on the container, so that if we scroll we scroll off the top of the container
  padding: theme.spacing(1, 0),

  // Dividers within menus don't go full width
  '& .MuiMenuItem-root + .MuiDivider-root': {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },

  // Menu subheaders
  '& .MuiListSubheader-root': {
    ...theme.typography['small-regular'],
    color: theme.palette.text.subtle,
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.bg.surface.elevated,
    // Increase the top pad, but offset with a negative margin
    // This means that the text doesn't move up at all when sticky
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(-1),
  },
})

export const menuItemIconStyle = (theme: Theme) => ({
  minWidth: 18,
  width: 18,
  marginRight: theme.spacing(2),
  color: theme.palette.fg.default,
  '& > svg': {
    fontSize: 18,
  },
})

export const MuiMenu: Components<Omit<Theme, 'components'>>['MuiMenu'] = {
  styleOverrides: {
    paper: ({ theme }) => menuContainerStyle(theme),
  },
}

export const MuiMenuItem: Components<Omit<Theme, 'components'>>['MuiMenuItem'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      // Duplicated in MuiAutocomplete option
      borderRadius: theme.shape['radius-sm'],
      padding: theme.spacing(0.5, 2),
      minHeight: '28px',
      ...theme.typography['body-regular'],
      color: theme.palette.text.default,
      '&:hover': {
        backgroundColor: theme.palette.bg.interactive.hover,
      },
      '&:not(:last-child)': {
        marginBottom: theme.spacing(0.5),
      },
      '&.Mui-disabled': {
        opacity: 0.4,
      },

      // Consistent (smaller) icon size and spacing
      '& .MuiListItemIcon-root': menuItemIconStyle(theme),
    }),
  },
  variants: [
    {
      props: { color: 'primary' },
      style: ({ theme }) => ({
        color: theme.palette.text.brand.default,
        '& .MuiListItemIcon-root': {
          color: theme.palette.fg.brand.default,
        },
      }),
    },
    {
      // Handle the deprecated "red" color
      props: (props) => props.color === 'danger' || props.color === 'red',
      style: ({ theme }) => ({
        color: theme.palette.text.danger.default,
        '& .MuiListItemIcon-root': {
          color: theme.palette.fg.danger.default,
        },
      }),
    },
  ],
}
